// SettingsTab — Phase 5 Task 15 port of docs/mockups/leaderboard-detail.html
// Settings state (L3589→3997), extended by
// .codelane/artifacts/t-65/leaderboard-settings-fields.html. Hosts the
// edit-mode LeaderboardConfigForm plus a Danger-zone card with Pause, Archive
// and Delete.
//
// The form is reused verbatim from LeaderboardConfigForm and edits a board's
// whole configuration; this file decides what of that a PATCH is allowed to
// carry. window_kind, scope, segmentation and tier_mode never travel — the
// cycles already cut and the rows already written belong to them. cron and the
// event dates travel only when they moved, which is what the operator asked
// for rather than what the form happens to be holding.
//
// Pause is reversible and applies immediately. Archive stops the board without
// taking anything away. Delete takes everything. The last two are gated behind
// a type-to-confirm modal that asks the operator to retype the board's ext_key.

import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";

import { Alert } from "../../../components/Alert/Alert";
import { Button } from "../../../components/Button/Button";
import { Modal } from "../../../components/Modal/Modal";
import { useOptionalToast } from "../../../components/Toast/toastContext";
import type { SchemaField } from "../../../components/SchemaEditor/SchemaEditor";
import { ApiError, describeError, errorReason } from "../../../lib/errors";
import {
  LeaderboardConfigForm,
  type SubmitMeta,
} from "../LeaderboardConfigForm";
import { windowLengthRefusal } from "../windowLength";
import {
  archiveLeaderboard,
  cancelPackage,
  cancelPackageField,
  deleteLeaderboard,
  dismissPackage,
  getDeleteImpact,
  getLadderImpact,
  getPendingPackage,
  getRetentionImpact,
  getStatKeyCoverage,
  lbKeys,
  listCycles,
  listRewards,
  schedulePackage,
  updateLeaderboard,
  type SchedulePackageInput,
} from "../api";
import { AppliedStrip, PendingBanner } from "../pendingBanner";
import {
  buildDiff,
  pickFields,
  realtimeOffCause,
  REALTIME_OFF_CONSEQUENCE,
} from "../pendingDiff";
import {
  boardHasNextCycle,
  fieldLabel,
  NEVER_SCHEDULED_FIELDS,
} from "../pendingFields";
import { PendingMarksCtx } from "../pendingContext";
import { LegendStrip, WhenBadge } from "../pendingMarks";
import { SaveNowConflictModal, SchedulePackageModal } from "../pendingModals";
import { nextCutOf } from "../pendingWhen";
import type {
  CreateLeaderboardInput,
  CreateRewardInput,
  LadderDecision,
  Leaderboard,
  PendingPackage,
  UpdateLeaderboardInput,
} from "../types";
import styles from "./SettingsTab.module.css";

// ──────────────────────────────────────────────────────────────────────────────
// Props
// ──────────────────────────────────────────────────────────────────────────────

export interface SettingsTabProps {
  gameId: string;
  lbId: string;
  lb: Leaderboard;
  /**
   * The project's `profile.stats_schema`, straight through to the form: on a
   * guild_score_source=player_stat board it is the list `guild_stat_key` is
   * picked from. The page above already reads the project for its breadcrumb,
   * so nothing is fetched twice.
   */
  statsSchema?: SchemaField[];
  /**
   * The board the operator was editing is not what they came to this page for
   * any more — it has just been archived, and stopped, or deleted, and is not
   * there at all. Where they go instead is the host's decision; this tab only
   * says that staying makes no sense.
   */
  onGone: () => void;
}

// ──────────────────────────────────────────────────────────────────────────────
// Field-projection helpers
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Does this timestamp say which zone it is in? The two sides of the event-date
 * conversion below both need to know, because the answer decides how a bare
 * `2026-09-01T10:00` is read — and reading it wrong moves the date by hours.
 */
const ZONED = /([Zz]|[+-]\d{2}:?\d{2})$/;

/**
 * `2026-09-01T10:00:00Z` → `2026-09-01T10:00`, the only shape a
 * `datetime-local` box accepts. The column is a timestamptz and the API speaks
 * RFC3339; the box has no zone at all, so the pair below agrees to read it as
 * UTC — the same clock the cron helper and the cycle dates are printed in.
 * Left alone when the string carries no zone: it is already in the box's shape,
 * and running it through Date would read it as local time and shift it.
 */
function toDateTimeLocal(iso: string | undefined): string | undefined {
  if (!iso || !ZONED.test(iso)) return iso;
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? iso : t.toISOString().slice(0, 16);
}

/** The way back: what the box holds, as the RFC3339 the API decodes. An
   unparseable draft is handed over untouched — the server's rejection names
   the field, and inventing a date here would name nothing. */
function fromDateTimeLocal(local: string | undefined): string | null {
  if (!local) return null;
  const t = new Date(ZONED.test(local) ? local : `${local}Z`);
  return Number.isNaN(t.getTime()) ? local : t.toISOString();
}

/** Map a Leaderboard row into a CreateLeaderboardInput shape for the form's
 *  `initial` prop. Straight copy of fields that exist on both DTOs, except the
 *  event dates, which change shape between the wire and the box. */
function leaderboardToFormInitial(lb: Leaderboard): Partial<CreateLeaderboardInput> {
  return {
    ext_key: lb.ext_key,
    name: lb.name,
    description: lb.description,
    scope: lb.scope,
    guild_aggregator: lb.guild_aggregator,
    guild_score_source: lb.guild_score_source,
    guild_stat_key: lb.guild_stat_key,
    min_guild_members: lb.min_guild_members,
    reward_contributors_only: lb.reward_contributors_only,
    late_joiners_may_score: lb.late_joiners_may_score,
    operator: lb.operator,
    sort: lb.sort,
    window_kind: lb.window_kind,
    cron: lb.cron,
    event_start_at: toDateTimeLocal(lb.event_start_at),
    event_end_at: toDateTimeLocal(lb.event_end_at),
    window_minutes: lb.window_minutes,
    segmentation: lb.segmentation,
    bucket_size: lb.bucket_size,
    roster_size: lb.roster_size,
    matchmaking_config: lb.matchmaking_config,
    tier_mode: lb.tier_mode,
    tier_config: lb.tier_config,
    realtime: lb.realtime,
    submit_policy: lb.submit_policy,
    guards: lb.guards,
    visibility: lb.visibility,
    metadata: lb.metadata,
    hold_finished_cycles: lb.hold_finished_cycles,
    retention_days: lb.retention_days,
    country_split: lb.country_split,
    auto_enroll: lb.auto_enroll,
  };
}

/**
 * Project the form's CreateLeaderboardInput payload down to the subset the
 * PATCH endpoint accepts, read against the board as it is stored.
 *
 * Three kinds of field pass through here:
 *
 *  - the ones that always travel (name, guards, visibility, metadata, …),
 *    joined now by `sort` — it used to be editable in the form and thrown away
 *    here, which made every change to it a lie;
 *  - the ones that travel only where they mean anything: `ext_key` on an
 *    archived board, `operator` on one the server does not derive it for;
 *  - `cron`, the event dates and the guild scoring pair
 *    (`guild_aggregator`, `guild_stat_key`), which travel only when they
 *    CHANGED. Their mere presence is an instruction — re-cut the schedule,
 *    move the window, rescore every guild of the project — and repeating the
 *    stored value asks for work nobody wanted, at the price of a 400
 *    (leaderboard_immutable_field) on a board whose cycle has already passed
 *    the date.
 *
 * window_kind, scope and tier_mode are in none of those groups: they are not
 * part of UpdateLeaderboardInput at all, because the cycles already cut and the
 * rows already written belong to the values the board was created with.
 *
 * `segmentation` used to be in that list and no longer is — but only halfway.
 * A cycle now carries its own stamp of the mode its rows are read by, so
 * bucket → personal can be saved without touching the week already being
 * played; every other move is still refused, and this builder only ever sends
 * the one.
 */
function buildUpdatePayload(
  form: CreateLeaderboardInput,
  lb: Leaderboard,
  meta: SubmitMeta,
): UpdateLeaderboardInput {
  const body: UpdateLeaderboardInput = {
    name: form.name,
    description: form.description,
    sort: form.sort,
    visibility: form.visibility,
    metadata: form.metadata,
  };
  // The four the form withholds on an auto-scored board, forwarded as the
  // absences they are. `operator` there is not merely pointless but refused —
  // the server derives it from guild_aggregator and answers 400 to a PATCH
  // that names one (validateUpdateOperatorPairs, R3). submit_policy and the
  // guards would be accepted, and that is the danger: resolving the absence to
  // a value here would overwrite a policy nobody was shown or wipe guards an
  // operator had set through the API. An omitted key leaves its column
  // exactly as it is.
  //
  // `realtime` is absent from every board without a room or a guild to send
  // rank.changed to, auto-scored ones included; there the server resets it.
  if (form.operator !== undefined) {
    body.operator = form.operator;
  }
  if (form.submit_policy !== undefined) {
    body.submit_policy = form.submit_policy;
  }
  if (form.guards !== undefined) {
    body.guards = form.guards;
  }
  if (form.realtime !== undefined) {
    body.realtime = form.realtime;
  }
  // The handle shipped game builds submit to. The form only opens the box on
  // an archived board — a live one answers a rename with 400
  // (leaderboard_immutable_field) — so what gates the key here is the board's
  // status, not whether the box differs.
  if (lb.status === "archived") {
    body.ext_key = form.ext_key;
  }
  // Naming `guild_aggregator` or `guild_stat_key` rescores every guild of the
  // project, which rewrites rows the open cycle has already answered with — so
  // both are schedule-only (ScheduleOnlyFields) and reach the board in a
  // package, at the cut, where there are no rows to contradict.
  //
  // Except on a board that will never cut another cycle, where a package has
  // no moment to land at. An auto-scored board is exactly that — the source
  // confines it to `all_time` — and holding the pair back there would mean a
  // stat key renamed in the project's schema kills the board for good. So on
  // those two windows the pair goes out immediately, still only when it MOVED:
  // the server rescores on the key's PRESENCE, without comparing it to the
  // stored value, and resending it on every save would make renaming a board
  // cost a full rescore of the project.
  const boardCuts = boardHasNextCycle(lb.window_kind);
  if (lb.scope === "guild_aggregated" && !boardCuts) {
    if (form.guild_aggregator && form.guild_aggregator !== lb.guild_aggregator) {
      body.guild_aggregator = form.guild_aggregator;
    }
    if (
      lb.guild_score_source === "player_stat" &&
      form.guild_stat_key &&
      form.guild_stat_key !== lb.guild_stat_key
    ) {
      body.guild_stat_key = form.guild_stat_key;
    }
  }
  if (lb.scope === "guild_aggregated") {
    // The team-battle board's own three. The first two travel on every save
    // rather than on a diff, so the columns end up saying exactly what the form
    // was showing, and they are cheap enough for that to need no argument — the
    // threshold is re-read when a team tries to enter, the fan-out flag when a
    // cycle closes.
    //
    // `late_joiners_may_score` travels only when it MOVED, and it is the same
    // split `guild_stat_key` above makes for the same reason: the server acts
    // on the field's PRESENCE, without comparing it to the stored value, so
    // resending it on every save would make renaming a board restate every
    // entered team of the open cycle and throw away that cycle's cached pages.
    // The server is right not to diff — a restatement that failed once must
    // stay askable — and the way to ask again is to move the switch, which is
    // exactly what an operator repairing it would do.
    //
    // The form only fills all three in on this source, and the server answers
    // 400 for them on any other, so the guard is the board's.
    if (lb.guild_score_source === "member_contribution") {
      if (form.min_guild_members !== undefined) {
        body.min_guild_members = form.min_guild_members;
      }
      if (form.reward_contributors_only !== undefined) {
        body.reward_contributors_only = form.reward_contributors_only;
      }
      if (
        form.late_joiners_may_score !== undefined &&
        form.late_joiners_may_score !== (lb.late_joiners_may_score ?? true)
      ) {
        body.late_joiners_may_score = form.late_joiners_may_score;
      }
    }
  }
  // An unchanged cron is not a harmless cron: the key's presence is what tells
  // the server to re-cut the schedule, and `cron_apply` says from when. Both
  // stay off the wire until the expression actually moved — sending it on
  // every save is what made a recurring board unsaveable in the first place.
  if (lb.window_kind === "recurring" && (form.cron ?? "") !== (lb.cron ?? "")) {
    body.cron = form.cron ?? null;
    body.cron_apply = meta.cronApply;
  }
  // A recurring_event schedule is two fields, and either of them alone is a
  // reschedule — so each travels on its own diff, and `cron_apply` travels
  // when either did. The server takes it beside a lone `window_minutes` for
  // exactly that reason (validateUpdateCron), and re-judges the overlap on the
  // PAIR the board ends up with, whichever half the body carried.
  // On a recurring_event board only half the schedule is still immediate. The
  // cron is read at the cut, so a new expression already shapes the next
  // window; the LENGTH is stamped on the cycle and now travels in a package
  // (ScheduleOnlyFields), which is why nothing about it is built here.
  if (lb.window_kind === "recurring_event") {
    if ((form.cron ?? "") !== (lb.cron ?? "")) {
      body.cron = form.cron ?? null;
      body.cron_apply = meta.cronApply;
    }
  }
  // Each date judged on its own, and against the stored value in the box's own
  // shape — comparing a box to an RFC3339 string would call every date changed
  // and ask to move a start the running cycle has already passed, which is a
  // 400 bought for nothing.
  if (lb.window_kind === "event") {
    const storedStart = toDateTimeLocal(lb.event_start_at) ?? "";
    const storedEnd = toDateTimeLocal(lb.event_end_at) ?? "";
    if ((form.event_start_at ?? "") !== storedStart) {
      body.event_start_at = fromDateTimeLocal(form.event_start_at);
    }
    if ((form.event_end_at ?? "") !== storedEnd) {
      body.event_end_at = fromDateTimeLocal(form.event_end_at);
    }
  }
  if (form.tier_config !== undefined) {
    body.tier_config = form.tier_config;
  }
  // Rooms — the one part of segmentation that moves after create, and only on
  // a tier board. `null` is a real instruction here (switch them off), which
  // is why the form sends it explicitly rather than omitting the key. On a
  // bucket board the size IS the segmentation and the backend answers 400
  // (leaderboard_immutable_field), so it is never forwarded from there.
  if (form.segmentation === "tier") {
    body.bucket_size = form.bucket_size ?? null;
  }
  // `segmentation` itself is not here any more: every move of it, including
  // the bucket → personal one a PATCH used to take, now waits for a cut
  // (ScheduleOnlyFields). The mode's own two fields are not schedule-only and
  // still travel immediately — on a board that is ALREADY personal.
  if (form.segmentation === "personal" && lb.segmentation === "personal") {
    // Both of the mode's own fields travel on every save, unlike the pair
    // above: neither rescores or re-cuts anything — a new length reaches the
    // lists at the next cycle (the open one holds its own in its stamp) and new
    // rules reach the lists assembled from now on — so the column ends up
    // saying exactly what the card was showing. On the switch itself they are
    // not optional at all: a board turned personal without them has no list
    // length and no rules, which the server refuses.
    //
    // What that costs, stated plainly: the save is LAST-WRITE-WINS on the
    // matchmaking rules. Saving any other setting on this page rewrites them
    // with what the form was holding when it was OPENED, so an edit made
    // meanwhile in another tab is overwritten. That is the same bargain every
    // always-sent field on this form makes (guards, submit_policy, the
    // member_contribution pair) — the page patches the board as a whole, and
    // only the fields with a SIDE EFFECT beyond their own column (cron, the
    // auto-score keys) are held back to a diff. Making this one field the
    // exception would need a deep compare of the config against the stored one
    // and would still leave every field beside it behaving the other way.
    if (form.roster_size !== undefined) {
      body.roster_size = form.roster_size;
    }
    if (form.matchmaking_config !== undefined) {
      body.matchmaking_config = form.matchmaking_config;
    }
  }
  // The cycle lifetime pair. The form omits both on a board where they mean
  // nothing (an all_time window, and the toggle also on a guild_aggregated
  // scope), and an omitted key is what leaves a column untouched — so the
  // absence is forwarded as an absence rather than resolved to a value here.
  if (form.hold_finished_cycles !== undefined) {
    body.hold_finished_cycles = form.hold_finished_cycles;
  }
  if (form.retention_days !== undefined) {
    body.retention_days = form.retention_days;
  }
  // The country view is the one setting here gated on segmentation that is
  // nonetheless patchable — turning it on stamps the open cycle's rows with
  // their country in the same transaction. The form leaves the key out on a
  // board the pair is refused on, and that absence travels as an absence for
  // the same reason the pair above does.
  if (form.country_split !== undefined) {
    body.country_split = form.country_split;
  }
  // Enrolment on read. The form leaves the key out only on a guild_aggregated
  // board, where the row is not shown at all; under sort=asc it sends `false`
  // instead of nothing, so that turning a board that enrols around to asc
  // saves as one PATCH rather than being refused for the pair.
  if (form.auto_enroll !== undefined) {
    body.auto_enroll = form.auto_enroll;
  }
  return body;
}

/** Human-readable text plus, when the backend sent one, its own wording —
   a single code (leaderboard_invalid_tier_config) stands for several
   different rules, so the reason is what makes the banner actionable. */
interface SaveError {
  message: string;
  reason: string | null;
}

function describeApiError(err: unknown): SaveError {
  if (err instanceof ApiError) {
    return { message: describeError(err.code), reason: errorReason(err) };
  }
  return { message: describeError("internal"), reason: null };
}

/** 8412 → "8 412". The counts in the delete dialog are the size of what is
   about to go, and a five-digit number nobody can read at a glance is not
   the place to save three characters. */
function formatCount(n: number): string {
  return n.toLocaleString("en-US").replace(/,/g, " ");
}

// ──────────────────────────────────────────────────────────────────────────────
// Package helpers
// ──────────────────────────────────────────────────────────────────────────────

/** A package about to be put on the board — what the confirmation dialog is
   showing, and what its Schedule button sends. */
interface PackageDraft {
  fields: Record<string, unknown>;
  ladderDecision?: LadderDecision;
}

/**
 * A row taken out of the package, and what its Undo would put back.
 *
 * The restore is a whole package write rather than a `{field: value}` pair
 * because the prize table is not a field: it travels in a column of its own,
 * and `{fields: {rewards: undefined}}` serialises to `{fields: {}}` — a
 * request that restores nothing while the toast promises otherwise.
 */
interface CancelledRow {
  field: string;
  label: string;
  restore: SchedulePackageInput;
}

/** An immediate save, and the package fields it takes down with it. */
interface ImmediateSave {
  body: UpdateLeaderboardInput;
  drops: string[];
}

/** The collision dialog's own copy of the same pair, before it is confirmed. */
type ConflictDraft = ImmediateSave;

const NEVER_SCHEDULED = new Set<string>(NEVER_SCHEDULED_FIELDS);

/**
 * The package body: every field the form is holding differently from the
 * board, at the value the form is holding.
 *
 * Read off the CHANGED list rather than off the payload, because the payload
 * states values for fields nobody touched — that is what makes a PATCH a
 * whole-board save — and a package built from it would schedule thirty
 * columns to be written with what they already hold.
 *
 * `ext_key` and `status` are dropped even if they moved: the server refuses
 * both in a package (NeverScheduledFields), and a key rename on an archived
 * board is an immediate save that has nothing to wait for anyway.
 */
function packageFields(
  input: CreateLeaderboardInput,
  changed: string[],
): Record<string, unknown> {
  const source = input as unknown as Record<string, unknown>;
  return pickFields(
    source,
    changed.filter((f) => !NEVER_SCHEDULED.has(f)),
  );
}

/**
 * The body with every field STRUCK OUT that the package is holding and the
 * operator did not touch.
 *
 * The two halves of this page disagree without it. This form names the cheap
 * fields on every save whether or not they moved — `guards`, `visibility`,
 * `auto_enroll`, the matchmaking rules — because none of them rescores or
 * re-cuts anything and the column then says exactly what the card was showing.
 * The server, meanwhile, strikes out of the pending package every field an
 * immediate PATCH NAMES (`immediateFieldNames`), on the rule that the last
 * explicit statement about a field wins.
 *
 * Put together, a save that came to change the cron silently deleted the
 * package's own row for every always-named field it happened to hold: an
 * operator who scheduled `auto_enroll: false` with a switch to personal lists,
 * then edited the schedule, was left with a package that no longer turns the
 * toggle off — and would be refused at the cut for it.
 *
 * `changedFields` is what the operator actually edited. A field in there that
 * the package also holds is a real collision and keeps its dialog below; a
 * field that is merely being resent has nothing to say and is not sent.
 */
function withoutPackagedFields(
  body: UpdateLeaderboardInput,
  waiting: PendingPackage | null,
  changed: string[],
): UpdateLeaderboardInput {
  if (!waiting) return body;
  const out = { ...body } as Record<string, unknown>;
  for (const field of Object.keys(waiting.fields)) {
    if (!changed.includes(field)) delete out[field];
  }
  return out as UpdateLeaderboardInput;
}

/**
 * A refused package as something the form can open on. Cast rather than
 * mapped: the keys ARE the form's own field names — that is what a package is
 * — and a mapping table here would be a second place to keep them in step.
 */
function seedFromPackage(
  fields: Record<string, unknown>,
): Partial<CreateLeaderboardInput> {
  return fields as Partial<CreateLeaderboardInput>;
}

// ──────────────────────────────────────────────────────────────────────────────
// SettingsTab — entry point
// ──────────────────────────────────────────────────────────────────────────────

export function SettingsTab({
  gameId,
  lbId,
  lb,
  statsSchema,
  onGone,
}: SettingsTabProps) {
  const qc = useQueryClient();
  // Optional on purpose: the Undo of a cancelled row is the one thing on this
  // page that needs a toast, and a tab that threw without a provider would be
  // unmountable in every host that has none.
  const toast = useOptionalToast();

  const [saveError, setSaveError] = useState<SaveError | null>(null);
  const [saveOk, setSaveOk] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState<"archive" | "delete" | null>(
    null,
  );

  // Both windows that cut cycles have a question the board alone cannot
  // answer. On an event board it is how much of the window is still ahead: a
  // started event has a start that is history, a finished one has both dates
  // behind it. On a recurring one it is the date the open cycle was announced
  // to close on — the thing the two `cron_apply` options are a choice between,
  // and the moment a scheduled package waits for.
  // `limit: 1` asks for the newest cycle, which in both cases is the one the
  // question is about. An all_time board has a single cycle that never closes
  // and nothing here reads it.
  const wantsCycle =
    lb.window_kind === "event" ||
    lb.window_kind === "recurring" ||
    lb.window_kind === "recurring_event";
  const cyclesQ = useQuery({
    queryKey: lbKeys.cycles(gameId, lbId, { limit: 1 }),
    queryFn: () => listCycles(gameId, lbId, { limit: 1 }),
    enabled: wantsCycle && !!gameId && !!lbId,
  });
  // `cycles` arrives as `null`, not `[]`, from a board that has none: the
  // repository builds a nil slice and Go marshals that as null. And a board
  // CAN have none — the retention sweep takes an event board's only cycle, and
  // it will never be given another. The index needs the same guard the answer
  // does, or reading `.status` off it throws in the middle of the render.
  const newestCycle = cyclesQ.data?.cycles?.[0];
  // Left undefined while the answer is missing — in flight, failed, or never
  // asked for. The form reads that as "not known yet" and keeps both dates
  // editable, which is what it did before anyone fetched cycles at all.
  const eventCycleStatus = newestCycle?.status;
  // Only from a cycle that is still open: the choice the form prints it in is
  // about an interval players are living through, and a cycle already closed
  // closes nothing any more.
  const openCycleCloseAt =
    newestCycle?.status === "open" ? newestCycle.close_at : undefined;
  // Its start, for the same cycle and under the same condition. On a
  // recurring_event board `cron_apply=current_cycle` moves the end to this
  // moment plus the new length, so it is the fact that lets the form name the
  // date instead of describing it.
  const openCycleOpenAt =
    newestCycle?.status === "open" ? newestCycle.open_at : undefined;
  // Between two windows: the newest cycle is one that has not started. Only a
  // recurring_event board reaches that state and stays in it — an event board's
  // scheduled cycle is its ONE window, and the form asks a different question
  // about that. It is passed explicitly rather than left for the form to infer
  // from two missing dates, because «no open cycle» and «the answer has not
  // arrived» are not the same thing and only this side can tell them apart.
  const windowPaused =
    lb.window_kind === "recurring_event" && newestCycle?.status === "scheduled";
  const nextCut = nextCutOf(lb.window_kind, newestCycle);

  // ---- The package ---------------------------------------------------------

  // Its own request, not part of the board: GET /leaderboards/{id} does not
  // carry it, and the great majority of boards have none — a 404 answered as
  // `null` (see getPendingPackage) rather than as an error nobody can act on.
  const pendingQ = useQuery({
    queryKey: lbKeys.pending(gameId, lbId),
    queryFn: () => getPendingPackage(gameId, lbId),
    enabled: !!gameId && !!lbId,
  });
  const pkg = pendingQ.data ?? null;
  /** Only a package still waiting marks the form. An applied one is on the
     board already; a refused one never will be. */
  const waiting = pkg?.status === "pending" ? pkg : null;

  // A claim_gated tier editor mirrors the tier_threshold reward bound to every
  // rung, and rewards are edited on the Rewards tab, not in this form — so the
  // form has to be handed them.
  //
  // A package carrying a prize table needs the same list for a different
  // reason, which is why the request is asked for here rather than beside the
  // board: without it the banner's table row has no "before" at all and reads
  // the absence as an empty board — "not set → 3 rows", "3 added" — on a board
  // that is already paying out those very three rows. It is asked below the
  // package on purpose: whether it is needed is the package's answer.
  const claimGated =
    lb.segmentation === "tier" && lb.tier_mode === "claim_gated";
  const rewardsQ = useQuery({
    queryKey: lbKeys.rewards(gameId, lbId),
    queryFn: () => listRewards(gameId, lbId),
    enabled: (claimGated || !!pkg?.rewards) && !!gameId && !!lbId,
  });
  /** The board's prize table, or `undefined` while nobody knows it — in
     flight, failed, or never asked for. Never resolved to `[]` here: that is
     a real table that pays nothing, and the two must not read alike. */
  const boardRewards = rewardsQ.data;

  /**
   * The package could not be read — which is not the same answer as "there is
   * none", though every branch below used to treat it as one. With `pkg` null
   * there are no `Scheduled:` badges, no banner and no collision dialog, so an
   * immediate save goes out blind and whatever is waiting overwrites it at the
   * next cut, silently.
   *
   * Only where a package could exist. A board that cuts no further cycle can
   * hold none — and has no second button to fall back on, so refusing the only
   * save it has would leave the page with no way forward at all.
   */
  const boardCuts = boardHasNextCycle(lb.window_kind);
  const packageUnknown = pendingQ.isError && boardCuts;

  // How many players hold a league, asked only on the board where the answer
  // decides something: a promotion ladder is the one thing here that outlives
  // a cycle. `retry: false` because a project whose backend does not answer
  // this yet should cost one request, not four — the question is still asked
  // without the numbers.
  const ladderQ = useQuery({
    queryKey: lbKeys.ladderImpact(gameId, lbId),
    queryFn: () => getLadderImpact(gameId, lbId),
    enabled:
      !!gameId &&
      !!lbId &&
      lb.segmentation === "tier" &&
      lb.tier_mode === "promotion",
    retry: false,
  });

  /**
   * What the form opens on. Normally the board; after "Edit and reschedule" it
   * is the board with the refused package laid over it, so the operator lands
   * on the values that were refused rather than on the ones that were kept.
   * The BASELINE stays the board either way — that is what makes those values
   * read as an unsaved edit instead of as nothing at all.
   */
  const [seed, setSeed] = useState<Partial<CreateLeaderboardInput> | undefined>(
    undefined,
  );
  /** Bumped to remount the form: its state is seeded once, at mount, so this
     is the only way to put values into it from out here. */
  const [formKey, setFormKey] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [scheduleDraft, setScheduleDraft] = useState<PackageDraft | null>(null);
  const [conflictDraft, setConflictDraft] = useState<ConflictDraft | null>(null);
  /** Package fields an otherwise successful save could not take out of the
     package. Named rather than counted so the warning can say which. */
  const [undropped, setUndropped] = useState<string[]>([]);

  function invalidatePending() {
    void qc.invalidateQueries({ queryKey: lbKeys.pending(gameId, lbId) });
  }

  const saveMutation = useMutation({
    mutationFn: async (save: ImmediateSave) => {
      const updated = await updateLeaderboard(gameId, lbId, save.body);
      // Setting a field by hand is the last word on it, so the value the
      // package was holding for the same field goes with the save. Sequential
      // rather than in parallel: each answer is the package that is left, and
      // the last one is the one the cache should keep.
      //
      // The PATCH above is what the save IS, and it has already happened by
      // the time this loop runs. So a refusal here is not a failed save and
      // must not be reported as one: an operator shown "Couldn't save changes"
      // over a board that has in fact changed presses the button again. The
      // names that would not come out are collected and reported as what they
      // really are — a leftover on a board that saved.
      const undropped: string[] = [];
      for (const field of save.drops) {
        try {
          await cancelPackageField(gameId, lbId, field);
        } catch {
          undropped.push(field);
        }
      }
      return { updated, undropped };
    },
    onSuccess: ({ undropped }) => {
      setSaveError(null);
      setSaveOk(true);
      setUndropped(undropped);
      setConflictDraft(null);
      void qc.invalidateQueries({ queryKey: lbKeys.detail(gameId, lbId) });
      // Prefix-key invalidation across all list filters. Invalidated, not
      // dropped: the board is still there and the operator stays on its page,
      // so the list is refetched by whoever mounts it next.
      void qc.invalidateQueries({ queryKey: lbKeys.listAll(gameId) });
      invalidatePending();
    },
    onError: (err) => {
      setSaveOk(false);
      setUndropped([]);
      // A refusal of the window length is answered under the field it is
      // about, not twice: the banner would repeat the same sentence at the top
      // of a page whose Scoring card already carries it, and the mockup draws
      // exactly one line. Every other refusal still lands here — the banner is
      // where an error with no field of its own belongs.
      setSaveError(windowLengthRefusal(err) ? null : describeApiError(err));
    },
  });

  const scheduleMutation = useMutation({
    mutationFn: (draft: PackageDraft) =>
      schedulePackage(gameId, lbId, {
        fields: draft.fields,
        // The prize table is the Rewards tab's business; `null` says "leave
        // whatever the package holds for it alone" rather than "clear it".
        rewards: null,
        ladder_decision: draft.ladderDecision,
      }),
    onSuccess: () => {
      setSaveError(null);
      setScheduleDraft(null);
      // The edit is in the package now, so the form goes back to showing the
      // board — with the scheduled values named under each field they will
      // overwrite, which is the whole point of the notes.
      setSeed(undefined);
      setFormKey((k) => k + 1);
      invalidatePending();
    },
    onError: (err) => {
      // The dialog closes on a refusal, whichever kind it is. It asked one
      // question — "schedule these?" — and it has been answered; leaving it up
      // with an error inside would make the operator dismiss a dialog to read
      // the page underneath. A refusal of the window length lands under the
      // field it is about, exactly as an immediate save's does, and everything
      // else lands in the banner at the top.
      setScheduleDraft(null);
      setSaveError(windowLengthRefusal(err) ? null : describeApiError(err));
    },
  });

  const cancelAllMutation = useMutation({
    mutationFn: () => cancelPackage(gameId, lbId),
    onSuccess: () => {
      setExpanded(false);
      invalidatePending();
    },
    onError: (err) => setSaveError(describeApiError(err)),
  });

  const cancelFieldMutation = useMutation({
    mutationFn: (row: CancelledRow) => cancelPackageField(gameId, lbId, row.field),
    onSuccess: (_data, row) => {
      invalidatePending();
      // The only Undo on this page, and the only place it can live: the toast
      // IS the notification that the row left the package.
      toast?.push({
        variant: "success",
        message: `${row.label} removed from the package`,
        action: (
          <button
            type="button"
            className={styles.toastUndo}
            onClick={() => undoMutation.mutate(row.restore)}
          >
            Undo
          </button>
        ),
      });
    },
    onError: (err) => setSaveError(describeApiError(err)),
  });

  const undoMutation = useMutation({
    mutationFn: (restore: SchedulePackageInput) =>
      schedulePackage(gameId, lbId, restore),
    onSuccess: invalidatePending,
    onError: (err) => setSaveError(describeApiError(err)),
  });

  const dismissMutation = useMutation({
    mutationFn: () => dismissPackage(gameId, lbId),
    onSuccess: invalidatePending,
    onError: (err) => setSaveError(describeApiError(err)),
  });

  const pauseMutation = useMutation({
    mutationFn: () => {
      const next: Leaderboard["status"] =
        lb.status === "paused" ? "active" : "paused";
      return updateLeaderboard(gameId, lbId, { status: next });
    },
    onSuccess: () => {
      setSaveError(null);
      void qc.invalidateQueries({ queryKey: lbKeys.detail(gameId, lbId) });
      void qc.invalidateQueries({ queryKey: lbKeys.listAll(gameId) });
      // Archiving a board cancels the package it is holding — server-side, so
      // the page only has to stop believing its copy.
      invalidatePending();
    },
    onError: (err) => {
      setSaveError(describeApiError(err));
    },
  });

  // Not a query: the form asks about a number the operator is still typing,
  // debounces it and drops answers that arrive after the number moved on. What
  // it needs from here is only the board this tab is about.
  const retentionImpact = useCallback(
    (days: number) => getRetentionImpact(gameId, lbId, days),
    [gameId, lbId],
  );

  // Same shape, and the same reason it is an arrow rather than a query: the
  // form asks about the key in the box, debounces it and drops answers that
  // arrive after the key moved on. Not about this board at all — the count is
  // over the project's players — so only the project id goes into it.
  const statKeyCoverage = useCallback(
    (key: string) => getStatKeyCoverage(gameId, key),
    [gameId],
  );

  /* `_rewards` is the form's create-mode business — in edit mode the Rewards
     tab owns them and the array is always empty. `meta` is not: it carries the
     decisions about the save that no column can hold, including which of the
     two buttons was pressed. */
  function handleSubmit(
    input: CreateLeaderboardInput,
    _rewards: CreateRewardInput[],
    meta: SubmitMeta,
  ) {
    setSaveOk(false);
    setSaveError(null);
    setUndropped([]);
    if (meta.intent === "schedule") {
      setScheduleDraft({
        fields: packageFields(input, meta.changedFields),
        ladderDecision: meta.ladderDecision,
      });
      return;
    }
    const body = withoutPackagedFields(
      buildUpdatePayload(input, lb, meta),
      waiting,
      meta.changedFields,
    );
    // A field the operator is setting by hand that the package is also holding
    // a value for. Both cannot be the last word, so the dialog says which one
    // wins before the save goes out rather than after.
    const drops = waiting
      ? meta.changedFields.filter((f) => f in waiting.fields)
      : [];
    if (drops.length > 0) {
      setConflictDraft({ body, drops });
      return;
    }
    saveMutation.mutate({ body, drops: [] });
  }

  // ---- What the package looks like on this board ---------------------------

  const pkgGroups = useMemo(
    () =>
      pkg
        ? buildDiff(
            // The prize table travels in a column of its own rather than among
            // the fields, because a package replaces it whole. It is still one
            // package and one banner, so it has to be folded back in here: a
            // table scheduled from the Rewards tab would otherwise show up on
            // this one as "0 changes scheduled".
            //
            // Only once the board's own table is known. Against `undefined`
            // the row would report that the board holds nothing and that every
            // row of the replacement is new — the wrong answer on a board
            // whose table is not moving at all — so while the answer is
            // missing the row is not drawn rather than drawn wrong.
            pkg.rewards && boardRewards
              ? { ...pkg.fields, rewards: pkg.rewards }
              : pkg.fields,
            lb,
            {
              blameField: pkg.failure_field,
              currentRewards: boardRewards,
              // An applied package's values are ON the board — that is what
              // applying it meant — and a refused one is a record of what was
              // turned down. Neither is a promise any more, so nothing about
              // the board can take a row away from it. Without this the strip
              // announces every applied package as "0 scheduled changes
              // applied".
              keepUnchanged: pkg.status !== "pending",
            },
          )
        : [],
    [pkg, lb, boardRewards],
  );
  const scheduleGroups = useMemo(
    () =>
      scheduleDraft
        ? buildDiff(
            scheduleDraft.ladderDecision
              ? {
                  ...scheduleDraft.fields,
                  ladder_decision: scheduleDraft.ladderDecision,
                }
              : scheduleDraft.fields,
            lb,
          )
        : [],
    [scheduleDraft, lb],
  );
  const conflictGroups = useMemo(
    () =>
      conflictDraft && waiting
        ? buildDiff(pickFields(waiting.fields, conflictDraft.drops), lb)
        : [],
    [conflictDraft, waiting, lb],
  );
  const packageBusy =
    cancelAllMutation.isPending ||
    cancelFieldMutation.isPending ||
    dismissMutation.isPending ||
    undoMutation.isPending;

  return (
    <div className={styles.wrap}>
      {saveOk && (
        <div className={styles.alertSlot}>
          {undropped.length > 0 ? (
            /* The board saved and the package did not let go. Reported as one
               thing rather than as a failure, because a failure banner over a
               board that HAS changed invites the operator to save again. */
            <Alert variant="warn" title="Saved, with scheduled changes left over">
              The board is saved, but {undropped.length}{" "}
              {undropped.length === 1
                ? "scheduled change"
                : "scheduled changes"}{" "}
              could not be dropped from the package:{" "}
              {undropped.map(fieldLabel).join(", ")}.{" "}
              {undropped.length === 1 ? "It still applies" : "They still apply"}{" "}
              at the next cut and would overwrite what you just saved. Take{" "}
              {undropped.length === 1 ? "it" : "them"} out from the banner
              below, or cancel the package.
            </Alert>
          ) : (
            <Alert variant="success" title="Saved">
              Configuration updated successfully.
            </Alert>
          )}
        </div>
      )}
      {saveError && (
        <div className={styles.alertSlot}>
          <Alert variant="danger" title="Couldn't save changes">
            {saveError.message}
            {saveError.reason && (
              <>
                {" "}
                <code className={styles.errorReason}>{saveError.reason}</code>
              </>
            )}
          </Alert>
        </div>
      )}

      {/* Not "there is no package" — nobody knows. Everything on this page
          that reads the package is missing while this is up, so the one save
          that could be silently undone by it is refused until the answer
          arrives. */}
      {packageUnknown && (
        <div className={styles.alertSlot}>
          <Alert variant="danger" title="Couldn't load the scheduled changes">
            This board may be holding a package of changes waiting for the next
            cut, and this page could not read it. Saving now would go out
            without knowing what it collides with, and whatever is waiting
            would overwrite it at the cut. Reload the page to ask again —
            scheduling still works, because a scheduled edit joins the package
            instead of racing it.
          </Alert>
        </div>
      )}

      {/* The package, in whichever of its three states it is in. Only one of
          the three is ever on the page: the row is unique per board. */}
      {pkg?.status === "applied" && (
        <AppliedStrip
          pkg={pkg}
          count={pkgGroups.reduce((n, g) => n + g.rows.length, 0)}
          auditHref="?tab=audit"
          onDismiss={() => dismissMutation.mutate()}
          busy={packageBusy}
        />
      )}
      {(waiting || pkg?.status === "failed") && pkg && (
        <PendingBanner
          pkg={pkg}
          groups={pkgGroups}
          nextCut={nextCut}
          expanded={expanded}
          onToggleExpanded={() => setExpanded((v) => !v)}
          onCancelAll={() => cancelAllMutation.mutate()}
          onCancelField={(field, label) =>
            cancelFieldMutation.mutate({
              field,
              label,
              restore:
                field === "rewards"
                  ? // The table is not in `pkg.fields` at all — it has a
                    // column of its own — so putting it back means writing
                    // that column again.
                    { fields: {}, rewards: pkg.rewards ?? [] }
                  : { fields: { [field]: pkg.fields[field] }, rewards: null },
            })
          }
          busy={packageBusy}
          onDismiss={() => dismissMutation.mutate()}
          onReschedule={() => {
            // The refused values go back into the form as an unsaved edit, and
            // the package that held them is gone: what refused was judged
            // against the board as it stands, and re-offering the same set
            // unedited would be refused again.
            setSeed(seedFromPackage(pkg.fields));
            setFormKey((k) => k + 1);
            dismissMutation.mutate();
          }}
        />
      )}

      {/* What a mark on a field means — the three answers, above the form that
          uses them. Edit mode only: a board being created has no cycle to
          wait for. */}
      <LegendStrip />

      <LeaderboardConfigForm
        key={formKey}
        mode="edit"
        initial={leaderboardToFormInitial(lb)}
        seed={seed}
        status={lb.status}
        eventCycleStatus={eventCycleStatus}
        openCycleCloseAt={openCycleCloseAt}
        openCycleOpenAt={openCycleOpenAt}
        windowPaused={windowPaused}
        pending={waiting}
        nextCut={nextCut}
        ladderImpact={ladderQ.data}
        scheduling={scheduleMutation.isPending}
        /* The one refusal this form can answer better than the banner above:
           the server measured the gap between two starts and the window did
           not fit inside it, which is a fact about the field the operator was
           editing. Anything else it says stays in the banner. */
        windowLengthError={
          windowLengthRefusal(saveMutation.error) ??
          windowLengthRefusal(scheduleMutation.error)
        }
        /* Passed raw on purpose: undefined is "not known yet", and the form
           needs to tell that apart from a board with no rewards. `?? []` here
           would make every first frame accuse a healthy ladder of breaking. */
        rewards={rewardsQ.data}
        fetchRetentionImpact={retentionImpact}
        statsSchema={statsSchema}
        fetchStatKeyCoverage={statKeyCoverage}
        saving={saveMutation.isPending}
        saveNowBlocked={
          packageUnknown
            ? "This board's scheduled changes couldn't be read — reload the page, or schedule this edit instead"
            : undefined
        }
        onSubmit={handleSubmit}
        onCancel={() => {
          /* Discard is the form's own business — it puts its state back where
             it opened. Nothing out here holds a copy of it. */
        }}
      />

      {scheduleDraft && (
        <SchedulePackageModal
          groups={scheduleGroups}
          nextCut={nextCut}
          saving={scheduleMutation.isPending}
          onConfirm={() => scheduleMutation.mutate(scheduleDraft)}
          onClose={() => setScheduleDraft(null)}
        />
      )}

      {conflictDraft && waiting && (
        <SaveNowConflictModal
          groups={conflictGroups}
          remaining={
            Object.keys(waiting.fields).length +
            // The prize table is one change of the same package — the banner
            // on this very page counts it as one — so a dialog that counted
            // only `fields` would tell a package of two fields and a table
            // that "the other 1 change is untouched".
            (waiting.rewards ? 1 : 0) -
            conflictDraft.drops.length
          }
          nextCut={nextCut}
          consequence={
            realtimeOffCause(
              conflictDraft.body as unknown as Record<string, unknown>,
              lb,
            )
              ? REALTIME_OFF_CONSEQUENCE
              : undefined
          }
          saving={saveMutation.isPending}
          onConfirm={() => saveMutation.mutate(conflictDraft)}
          onClose={() => setConflictDraft(null)}
        />
      )}

      {/* The one field that goes the other way: a pause that waited for the
          next cycle would not be a pause. It has no row in the form — pausing
          and archiving are what the Danger zone is — so the mark goes on the
          row that does it, under a context that says the marks are live. */}
      <PendingMarksCtx.Provider
        value={{ pending: null, marked: true, cuts: boardCuts }}
      >
      <DangerZone
        lb={lb}
        onPauseToggle={() => pauseMutation.mutate()}
        pausePending={pauseMutation.isPending}
        onArchiveClick={() => setConfirmOpen("archive")}
        onDeleteClick={() => setConfirmOpen("delete")}
      />
      </PendingMarksCtx.Provider>

      {confirmOpen === "archive" && (
        <ArchiveConfirmModal
          gameId={gameId}
          lbId={lbId}
          lb={lb}
          onClose={() => setConfirmOpen(null)}
          onArchived={() => {
            setConfirmOpen(null);
            onGone();
          }}
        />
      )}

      {confirmOpen === "delete" && (
        <DeleteConfirmModal
          gameId={gameId}
          lbId={lbId}
          lb={lb}
          onClose={() => setConfirmOpen(null)}
          onDeleted={() => {
            setConfirmOpen(null);
            onGone();
          }}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Danger zone card
// ──────────────────────────────────────────────────────────────────────────────

function DangerZone({
  lb,
  onPauseToggle,
  pausePending,
  onArchiveClick,
  onDeleteClick,
}: {
  lb: Leaderboard;
  onPauseToggle: () => void;
  pausePending: boolean;
  onArchiveClick: () => void;
  onDeleteClick: () => void;
}) {
  // Pausing and archiving are both about a board that still runs, and an
  // archived one has neither left to offer. Deleting is the row that survives
  // that: an archived board is precisely the one an operator is most likely to
  // want gone for good, so the zone can never be a dead end.
  const archived = lb.status === "archived";
  const isPaused = lb.status === "paused";
  const pauseLabel = isPaused ? "Resume leaderboard" : "Pause leaderboard";
  const pauseDesc = isPaused
    ? "Resume the leaderboard — start accepting new submits again."
    : "Pause the leaderboard — stop accepting new submits while keeping the current cycle visible.";

  return (
    <section className={styles.dangerZone} aria-label="Danger zone">
      <div className={styles.dzHead}>
        <AlertTriangle size={16} aria-hidden="true" />
        <h3>Danger zone</h3>
      </div>

      {archived ? (
        <div className={styles.dzMuted}>
          This leaderboard is already archived. Existing cycles remain readable.
        </div>
      ) : (
        <>
          <div className={styles.dzRow}>
            <div className={styles.dzBody}>
              <h4>
                {pauseLabel}
                <WhenBadge field="status" />
              </h4>
              <p>{pauseDesc}</p>
              <p>
                A pause that waited for the next cycle would not be a pause.
                This one field never travels in a package — and archiving a
                board cancels the package it is holding.
              </p>
            </div>
            <Button
              variant={isPaused ? "primary" : "secondary"}
              onClick={onPauseToggle}
              loading={pausePending}
            >
              {pauseLabel}
            </Button>
          </div>

          <div className={styles.dzRow}>
            <div className={styles.dzBody}>
              <h4>Archive leaderboard</h4>
              <p>
                Archive the leaderboard — permanent. Existing cycles and scores
                remain readable, but no new cycles or submits will run.
              </p>
            </div>
            <Button variant="danger" onClick={onArchiveClick}>
              Archive leaderboard
            </Button>
          </div>
        </>
      )}

      <div className={styles.dzRow}>
        <div className={styles.dzBody}>
          <h4>Delete leaderboard</h4>
          <p>
            Delete the leaderboard and everything inside it — cycles, scores,
            rooms, rewards and the grants players haven't claimed. Nothing is
            kept and nothing can be restored.
          </p>
        </div>
        <Button variant="danger" onClick={onDeleteClick}>
          Delete leaderboard
        </Button>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Archive confirm modal (type-to-confirm)
// ──────────────────────────────────────────────────────────────────────────────

function ArchiveConfirmModal({
  gameId,
  lbId,
  lb,
  onClose,
  onArchived,
}: {
  gameId: string;
  lbId: string;
  lb: Leaderboard;
  onClose: () => void;
  onArchived: () => void;
}) {
  const qc = useQueryClient();
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<SaveError | null>(null);

  const mutation = useMutation({
    mutationFn: () => archiveLeaderboard(gameId, lbId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: lbKeys.detail(gameId, lbId) });
      // Dropped, not invalidated: `onArchived` takes the operator to the list,
      // which is not mounted yet — an invalidation would only flag it, and on
      // mount the cache is served first. The list filters by status = active
      // by default, so the row this board still has there is a row of a board
      // that no longer belongs in the view.
      qc.removeQueries({ queryKey: lbKeys.listAll(gameId) });
      onArchived();
    },
    onError: (err) => {
      setError(describeApiError(err));
    },
  });

  // Trimmed on both sides, and never on an empty key: the ext_key is meant to
  // be copied out of the lines around the field — one click takes it whole —
  // and a paste that carries a trailing space is not a different board. The
  // deletion dialog below and the three name-typing dialogs agree.
  const wanted = lb.ext_key.trim();
  const canConfirm =
    wanted !== "" && typed.trim() === wanted && !mutation.isPending;
  const inputId = "archive-confirm-ext-key";

  return (
    <Modal
      open
      onClose={onClose}
      title="Archive leaderboard?"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => mutation.mutate()}
            disabled={!canConfirm}
            loading={mutation.isPending}
          >
            Archive
          </Button>
        </>
      }
    >
      <p className={styles.modalBodyText}>
        This will archive <b>{lb.name}</b> (<code>{lb.ext_key}</code>).
        Existing cycles and scores remain readable, but no new cycles or
        submits will run. This action is permanent.
      </p>

      <label className={styles.modalLabel} htmlFor={inputId}>
        Type the ext_key to confirm
      </label>
      <input
        id={inputId}
        className={styles.input}
        autoComplete="off"
        spellCheck={false}
        placeholder={lb.ext_key}
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
      />
      <span className={styles.helper}>
        Type <code>{lb.ext_key}</code> exactly to enable the Archive button.
      </span>

      {error && (
        <div className={styles.errorBlurb} role="alert">
          {error.message}
          {error.reason && (
            <>
              {" "}
              <code className={styles.errorReason}>{error.reason}</code>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Delete confirm modal (type-to-confirm + impact counts)
// ──────────────────────────────────────────────────────────────────────────────

/** One line of "will be deleted". The number is missing while the count is
   still being asked for — a zero there would be an answer, and the wrong one
   on every board. */
function ImpactRow({
  value,
  muted,
  children,
}: {
  value: number | undefined;
  muted?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={muted ? `${styles.pfItem} ${styles.pfMuted}` : styles.pfItem}>
      <span className={styles.pfNum}>
        {value === undefined ? "…" : formatCount(value)}
      </span>
      <span>{children}</span>
    </div>
  );
}

function DeleteConfirmModal({
  gameId,
  lbId,
  lb,
  onClose,
  onDeleted,
}: {
  gameId: string;
  lbId: string;
  lb: Leaderboard;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const qc = useQueryClient();
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<SaveError | null>(null);

  // Asked when the dialog opens rather than with the board, because it is the
  // one thing on this screen nobody needs until they are about to destroy
  // something — and it is a count over every cycle the board ever had.
  const impactQ = useQuery({
    queryKey: lbKeys.deleteImpact(gameId, lbId),
    queryFn: () => getDeleteImpact(gameId, lbId),
    enabled: !!gameId && !!lbId,
  });
  const impact = impactQ.data;

  const mutation = useMutation({
    mutationFn: () => deleteLeaderboard(gameId, lbId),
    onSuccess: () => {
      // Dropped, not invalidated: invalidating asks for a board that is not
      // there any more, and the 404 would land as an error on a screen the
      // operator is already leaving.
      qc.removeQueries({ queryKey: lbKeys.detail(gameId, lbId) });
      qc.removeQueries({ queryKey: lbKeys.deleteImpact(gameId, lbId) });
      // The cached lists go the same way, across every filter: `onDeleted`
      // leaves for the list, and it is not mounted to be refetched by an
      // invalidation — it would open on its cache and draw the deleted board
      // for a frame. A skeleton is the honest first frame here.
      qc.removeQueries({ queryKey: lbKeys.listAll(gameId) });
      onDeleted();
    },
    onError: (err) => {
      setError(describeApiError(err));
    },
  });

  // Trimmed on both sides, and never on an empty key — as in the archive
  // dialog above.
  const wanted = lb.ext_key.trim();
  const canConfirm =
    wanted !== "" && typed.trim() === wanted && !mutation.isPending;
  const inputId = "delete-confirm-ext-key";

  return (
    <Modal
      open
      onClose={onClose}
      title="Delete leaderboard?"
      /* 460px, the width the mockup draws this dialog at (§5 .modal). It
         carries more than the archive one it was copied from: a danger banner,
         four consequence rows whose numbers sit in a 54px column, the lead, the
         confirmation box and its caption. At the default 400 the rows wrap. */
      width="sm"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => mutation.mutate()}
            disabled={!canConfirm}
            loading={mutation.isPending}
          >
            Delete
          </Button>
        </>
      }
    >
      {/* Only a live board can be taking submits while this dialog is open;
          a paused or archived one stopped answering the SDK long ago, and
          warning about it there would be noise on the screen that needs the
          operator's attention most. */}
      {lb.status === "active" && (
        <div className={styles.modalAlertSlot}>
          <Alert variant="danger" title="This leaderboard is live">
            It is <code>active</code> and may be taking submits right now. The
            SDK will start getting <code>leaderboard_not_found</code> the moment
            it is gone.
          </Alert>
        </div>
      )}

      <p className={styles.modalBodyText}>
        This deletes <b>{lb.name}</b> (<code>{lb.ext_key}</code>) and everything
        it holds. This cannot be undone.
      </p>

      <div className={styles.pfLabel}>Will be deleted</div>
      {impactQ.isError ? (
        <p className={styles.pfFailed}>
          Couldn't count what goes with the board. Deleting still takes every
          cycle, score, reward and unclaimed grant it holds.
        </p>
      ) : (
        <div className={styles.pfList} aria-busy={impact === undefined}>
          <ImpactRow value={impact?.cycles}>
            {lb.status === "archived"
              ? "cycles"
              : "cycles, including the one open now"}
          </ImpactRow>
          <ImpactRow value={impact?.score_rows}>score rows</ImpactRow>
          <ImpactRow value={impact?.unclaimed_grants}>
            rewards nobody has claimed yet
          </ImpactRow>
          {/* Muted because it is the only line here that is configuration
              rather than something players earned. */}
          <ImpactRow value={impact?.rewards} muted>
            reward definitions
          </ImpactRow>
        </div>
      )}

      <label className={styles.modalLabel} htmlFor={inputId}>
        Type the ext_key to confirm
      </label>
      <input
        id={inputId}
        className={styles.input}
        autoComplete="off"
        spellCheck={false}
        placeholder={lb.ext_key}
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
      />
      <span className={styles.helper}>
        Type <code>{lb.ext_key}</code> exactly to enable the Delete button.
      </span>

      {error && (
        <div className={styles.errorBlurb} role="alert">
          {error.message}
          {error.reason && (
            <>
              {" "}
              <code className={styles.errorReason}>{error.reason}</code>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
